import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { BehaviorSubject,  Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { ConditionListsAgreementModel } from "../shared/models/components/condition-lists-agreement.model";
import { ProductMtaAgreementModel } from "../shared/models/components/product-mta-agreement.model";
import { ProductConditionListCheckModel } from "../shared/models/condition-lists/product-condition-list-check.model";
import { ProductMtaModel } from "../shared/models/mta/product-mta.model";
import { ConditionListsPopUpService } from "../shared/services/components/condition-lists-pop-up.service";
import { ProductMtaPopUpService } from "../shared/services/components/product-mta-pop-up.service";
import { ProductCartQuantityService } from "../shared/services/components/product-cart-quantity.service";
import { RegisterInterestPopUpService } from "../shared/services/components/register-interest-pop-up.service";
import { Pending } from "../shared/decorators/pending.decorator";
import { DisposeMixin } from "../shared/mixins/dispose.mixin";
import { LockMixin } from "../shared/mixins/lock.mixin";
import { EmptyMixin } from "../shared/mixins/empty.mixin";
import { AddToCartResultModel } from "../shared/models/cart/add-to-cart-result.model";
import { ProductModel } from "../shared/models/product/product.model";
import { NotificationService } from "../shared/services/components/notification.service";
import { ConfigurationService } from "../shared/services/configuration.service";
import { ProductCartService } from "../shared/services/product/product-cart.service";
import { ProductService } from "../shared/services/product/product.service";
import { WindowRefService } from "../shared/services/window-ref.service";
import { ProductLineComponent } from "./components/product-line/product-line.component";
import { ProductSearchForm } from "./forms/product-search.form";
import { QuickShopForm } from "./forms/quick-shop.form";
import { ProductSearchFormModel } from "./models/forms/product-search-form.model";
import { QuickShopFormModel } from "./models/forms/quick-shop-form.model";
import { QuickShopConfigModel } from "./models/quick-shop-config.model";
import { DistributionRuleModel } from "../shared/models/cart/distribution-rule.model";

@Component({
    selector: "pcg-ng-nop-quick-shop",
    template: require('./quick-shop.component.html')
})
export class QuickShopComponent extends DisposeMixin(LockMixin(EmptyMixin)) implements OnInit, OnDestroy {
    private searchSubscription: Subscription;
    private submitSubscription: Subscription;

    @Pending
    private getProductSubscription: Subscription;

    @Pending
    private getConditionsSubscription: Subscription;

    @Pending
    private getMtasSubscription: Subscription;

    @Pending
    private addToCartSubscription: Subscription;

    private checkSubscription: Subscription;

    @ViewChildren("productLine")
    public productLines: QueryList<ProductLineComponent>;

    public searchForm: ProductSearchForm;
    public quickShopForm: QuickShopForm;

    public products: Array<ProductModel> = new Array<ProductModel>();

    public showDiscontinuedResultAlert: boolean;
    public showEmptySearchResultAlert: boolean;
    public distributionRule: DistributionRuleModel;
    private lastProduct: ProductModel;

    public get showDistributionRuleAlert(): boolean {
        return this.redirectRules.length > 0;
    }

    public get directDistributionRuleAvailable(): boolean {
        return this.lastProduct.distributionRules.some(r => r.isDirectRule);
    }

    public get redirectRules(): Array<DistributionRuleModel> {
        return this.lastProduct.distributionRules.filter(r => !r.isDirectRule && !r.isDepositor);
    }

    public get depositorRules(): Array<DistributionRuleModel> {
        return this.lastProduct.distributionRules.filter(r => r.isDepositor);
    }

    public constructor(
        private domElement: ElementRef,
        private formBuilder: FormBuilder,
        private configService: ConfigurationService<QuickShopConfigModel>,
        private windowRefService: WindowRefService,
        private notificationService: NotificationService,
        private productService: ProductService,
        private productCartService: ProductCartService,
        private registerInterestPopUpService: RegisterInterestPopUpService,
        private conditionListsPopUpService: ConditionListsPopUpService,
        private productMtaPopUpService: ProductMtaPopUpService,
        private productCartQuantityService: ProductCartQuantityService
    ) {
        super();
        this.configService.initialize(this.domElement.nativeElement.dataset);
    }

    public ngOnInit(): void {
        this.searchForm = ProductSearchForm.createForm(this.formBuilder, new ProductSearchFormModel());
        this.searchSubscription = this.searchForm.changeObservable
            .subscribe(model => this.handleSearch(model));

        this.quickShopForm = QuickShopForm.createForm(this.formBuilder);
        this.submitSubscription = this.quickShopForm.submitObservable
            .subscribe(model => this.handleSubmit(model));
    }

    public ngOnDestroy(): void {
        this.dispose();
    }

    private handleSearch(model: ProductSearchFormModel): void {
        this.showEmptySearchResultAlert = false;
        this.showDiscontinuedResultAlert = false;
        this.getProductSubscription = this.productService.getProductByCatalogNumber(model.catalogueNumber)
            .subscribe(product => this.processSearchResult(product));
    }

    private processSearchResult(product: ProductModel): void {
        this.lastProduct = product;
        if (product) {
            if (!this.directDistributionRuleAvailable) {
                // Do nothing
            } else if (product.accessioning) {
                this.registerInterestPopUpService.showPopUp(product);
            } else if (product.discontinued) {
                this.showDiscontinuedResultAlert = true;
            } else {
                this.products.push(product);
                this.quickShopForm.addProduct(product);
                this.notificationService.showSuccess("", "Product " + product.catalogueNumber + " found");
            }
        } else {
            this.showEmptySearchResultAlert = true;
        }
    }

    public handleRemoveClick(index: number): void {
        this.quickShopForm.removeProduct(index);
        this.products.splice(index, 1);
    }

    public canAddToCart(): boolean {
        return this.quickShopForm.formInstance.valid
            && !this.productLines.find(x => x.isLocked)
            && !this.isLocked;
    }

    public handleAddToCartClick(): void {
        this.quickShopForm.submit();
    }

    private handleSubmit(model: QuickShopFormModel): void {
        const productIds = model.formats.map(f => f.productId);

        const addToCartSubscribe = (formats: any) => {
            return this.addToCartSubscription = this.productCartService
                .addSingleFormatsListToCart(formats)
                .subscribe(result => this.processSubmitResult(result));
        };

        const handleGetConditionsSubscription = (previouslyAcceptedFormats: any) => {
            const formats = previouslyAcceptedFormats;
            this.getConditionsSubscription = this.productService.checkConditions(productIds)
            .subscribe(checkResult => {
                if (!checkResult.length) {
                    addToCartSubscribe(formats);
                    return;
                }

                let acceptedFormats = formats.filter((f: any) => checkResult.findIndex(cr => cr.productId === f.productId) === -1);
                let currentIndex = 0;
                let currentItem = checkResult[currentIndex];
                let acceptedCount = 0;
                const checkSubject = new BehaviorSubject<ProductConditionListCheckModel>(currentItem);

                this.checkSubscription = checkSubject
                    .pipe(finalize(() => {
                        if (acceptedCount > 0) {
                            addToCartSubscribe(acceptedFormats);
                        }
                    }))
                    .subscribe(checkResultItem => {
                        const showNextPopUp = () => {
                            this.conditionListsPopUpService.hidePopup();

                            if (checkResult.length - 1 > currentIndex) {
                                currentIndex++;
                                currentItem = checkResult[currentIndex];
                                checkSubject.next(currentItem);
                            } else {
                                checkSubject.complete();
                            }
                        };

                        const currentProduct = this.products.find(p => p.id === checkResultItem.productId);
                        this.conditionListsPopUpService.showPopUp(new ConditionListsAgreementModel (
                            currentProduct,
                            checkResultItem,
                            () => {
                                const acceptedCheckedFormats = model.formats.filter(f => f.productId === checkResultItem.productId);
                                acceptedFormats = acceptedFormats.concat(acceptedCheckedFormats);
                                acceptedCount++;
                                showNextPopUp();
                            },
                            () => showNextPopUp()
                        ));
                    });
            });
        };

        this.getMtasSubscription = this.productService.checkMta(productIds)
            .subscribe(checkResult => {
                if (!checkResult.length) {
                    handleGetConditionsSubscription(model.formats);
                    return;
                }

                let acceptedFormats = model.formats.filter(f => checkResult.findIndex(cr => cr.productId === f.productId) === -1);
                let currentIndex = 0;
                let currentItem = checkResult[currentIndex];
                let acceptedCount = 0;
                const checkSubject = new BehaviorSubject<ProductMtaModel>(currentItem);

                this.checkSubscription = checkSubject
                    .pipe(finalize(() => {
                        if (acceptedCount > 0) {
                            handleGetConditionsSubscription(acceptedFormats);
                        }
                    }))
                    .subscribe(checkResultItem => {
                        const showNextPopUp = () => {
                            this.productMtaPopUpService.hidePopup();

                            if (checkResult.length - 1 > currentIndex) {
                                currentIndex++;
                                currentItem = checkResult[currentIndex];
                                checkSubject.next(currentItem);
                            } else {
                                checkSubject.complete();
                            }
                        };

                        const currentProduct = this.products.find(p => p.id === checkResultItem.productId);
                        this.productMtaPopUpService.showPopUp(new ProductMtaAgreementModel (
                            currentProduct,
                            [checkResultItem],
                            () => {
                                const acceptedCheckedFormats = model.formats.filter(f => f.productId === checkResultItem.productId);
                                acceptedFormats = acceptedFormats.concat(acceptedCheckedFormats);
                                acceptedCount++;
                                showNextPopUp();
                            },
                            () => showNextPopUp()
                        ));
                    });
            });
    }

    private processSubmitResult(result: AddToCartResultModel): void {
        const config = this.configService.config;
        if (config.redirectUrl) {
            const window = this.windowRefService.nativeWindow;
            window.location.href = config.redirectUrl;
        } else {
            this.productCartQuantityService.updateCartQuantity(result.cartQuantity);
            this.notificationService.showSuccess("", result.userMessage);
            this.quickShopForm.reset();
            this.products = new Array<ProductModel>();
        }
    }
}
